/**
 * Save checkout personal data to localStorage (not cookies)
 * Excludes sensitive payment information
 * This is called immediately when form fields change (onChange and onBlur)
 * SSR-safe: only runs in browser environment
 */
export function saveCheckoutPersonalData(personal) {
    try {
        // SSR check: ensure we're in browser environment
        if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
            return;
        }

        if (!personal || typeof personal !== 'object') {
            console.warn('Invalid personal data to save:', personal);
            return;
        }

        localStorage.setItem('checkout_personal', JSON.stringify(personal));
        console.log('✓ Saved personal data to localStorage');
    } catch (error) {
        console.error('Error saving checkout personal data:', error);
    }
}

/**
 * Save checkout shipping data to localStorage (not cookies)
 * This is called immediately when form fields change (onChange and onBlur)
 * SSR-safe: only runs in browser environment
 */
export function saveCheckoutShippingData(shipping) {
    try {
        // SSR check: ensure we're in browser environment
        if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
            return;
        }

        if (!shipping || typeof shipping !== 'object') {
            console.warn('Invalid shipping data to save:', shipping);
            return;
        }

        localStorage.setItem('checkout_shipping', JSON.stringify(shipping));
        console.log('✓ Saved shipping data to localStorage');
    } catch (error) {
        console.error('Error saving checkout shipping data:', error);
    }
}

/**
 * Restore checkout personal data from localStorage
 * Called on app initialization
 * SSR-safe: returns empty object on server, actual data on client
 */
export function restoreCheckoutPersonalData() {
    try {
        // SSR check: return empty object on server
        if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
            return {};
        }

        const savedData = localStorage.getItem('checkout_personal');
        if (savedData) {
            const personal = JSON.parse(savedData);
            if (personal && typeof personal === 'object') {
                console.log('✓ Restored personal data from localStorage:', personal);
                return personal;
            }
        }
        return {};
    } catch (error) {
        console.error('Error restoring checkout personal data:', error);
        return {};
    }
}

/**
 * Restore checkout shipping data from localStorage
 * Called on app initialization
 * SSR-safe: returns empty object on server, actual data on client
 */
export function restoreCheckoutShippingData() {
    try {
        // SSR check: return empty object on server
        if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
            return {};
        }

        const savedData = localStorage.getItem('checkout_shipping');
        if (savedData) {
            const shipping = JSON.parse(savedData);
            if (shipping && typeof shipping === 'object') {
                console.log('✓ Restored shipping data from localStorage:', shipping);
                return shipping;
            }
        }
        return {};
    } catch (error) {
        console.error('Error restoring checkout shipping data:', error);
        return {};
    }
}

/**
 * Clear all saved checkout data from localStorage
 * Typically called after successful order completion
 * SSR-safe: only runs in browser environment
 */
export function clearCheckoutData() {
    try {
        // SSR check: ensure we're in browser environment
        if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
            return;
        }

        localStorage.removeItem('checkout_personal');
        localStorage.removeItem('checkout_shipping');
        console.log('✓ Cleared checkout data from localStorage');
    } catch (error) {
        console.error('Error clearing checkout data:', error);
    }
}

